import type { PostOauthResponse } from '@libs/data-access/queries'
import { authService } from '@libs/data-access/queries'

import type { AuthState } from '../AuthState.types'
import { setAuthCookies, syncAuthState } from '../authServiceUtils'

import { useAuthState } from './useAuthState'
import { useClearAuth } from './useClearAuth'
import { useIsAccessTokenExpiredSoon } from './useIsAccessTokenExpiredSoon'

/**
 * Manages the complete auth token refresh flow:
 * - Checks if token needs refresh
 * - Refreshes tokens
 * - Updates auth state
 * - Syncs http services
 * - Sets auth cookies
 * - Handles sign-out on failure
 *
 * Note: The tokens are stored in http-only cookies for security.
 * This hook ensures both the access_token and refresh_token are properly refreshed
 * and the auth state is synchronized across the application.
 */
export const useAuthTokenRefresh = () => {
  const { updateAuthState } = useAuthState()
  const { isTokenExpiredSoon } = useIsAccessTokenExpiredSoon()
  const { mutateAsync, isPending } = authService.usePostOauthRefreshToken()
  const clearAuth = useClearAuth({ redirectTo: true })

  return {
    isRefreshing: isPending || isTokenExpiredSoon,
    process: async (): Promise<PostOauthResponse | undefined> => {
      if (!isTokenExpiredSoon) {
        return
      }

      try {
        const res = await mutateAsync()

        const newAuthState = {
          accessTokenExpiresAt: res.expires_at,
          isAccountCreationInProgress: res.info.is_account_creation_in_progress
        }

        updateAuthState(newAuthState)
        syncAuthState(newAuthState as AuthState)
        setAuthCookies(newAuthState as AuthState)

        return res
      } catch {
        /**
         * If the request fails, then we sign-out the user
         * It means that the refresh_token was not valid anymore
         */
        clearAuth()
      }
    }
  }
}
